import type { Board, Member } from '@trello/model-types';

import { allowsSelfJoin } from '../board/allowsSelfJoin';
import { isMemberOfBoard } from '../board/isMemberOfBoard';
import { isMemberTypeOfBoard } from '../board/isMemberTypeOfBoard';
import { getMembershipTypeFromOrganization } from '../organization/getMembershipTypeFromOrganization';

/**
 * Checks if a user has permission to comment or react to a board/card notification.
 */
export const canComment = (
  member: Pick<
    Member,
    'id' | 'idEnterprisesAdmin' | 'idPremOrgsAdmin' | 'memberType'
  >,
  board: {
    idOrganization?: string | null;
    organization?:
      | (Pick<Board['organization'], 'id' | 'offering'> & {
          memberships: Pick<
            Board['organization']['memberships'][number],
            'deactivated' | 'idMember' | 'memberType' | 'unconfirmed'
          >[];
        })
      | null;
    prefs?: Pick<
      Partial<Board['prefs']>,
      'comments' | 'isTemplate' | 'selfJoin'
    > | null;
    memberships: Pick<
      Board['memberships'][number],
      'deactivated' | 'idMember' | 'memberType' | 'unconfirmed'
    >[];
  },
): boolean => {
  if (!board.prefs?.comments) {
    return false;
  }

  const isMember = isMemberOfBoard(board, member.id);
  const isMemberOrg =
    !!board?.idOrganization &&
    !!board?.organization &&
    ['admin', 'normal'].includes(
      getMembershipTypeFromOrganization(member, board.organization) || '',
    );
  const isMemberObserver = isMemberTypeOfBoard(member, board, 'observer');
  const isImplicitAdmin = isMemberTypeOfBoard(member, board, 'admin');
  const isBoardWorkspaceEditable = allowsSelfJoin(board);

  switch (board.prefs.comments) {
    case 'public':
      return !!member.id;
    case 'org':
      return isMember || isMemberOrg || isMemberObserver;
    case 'observers':
      return (
        isMember ||
        isMemberObserver ||
        isImplicitAdmin ||
        (isMemberOrg && isBoardWorkspaceEditable)
      );
    case 'members':
      return (
        (isMember && !isMemberObserver) ||
        isImplicitAdmin ||
        (isMemberOrg && isBoardWorkspaceEditable)
      );
    default:
      return false;
  }
};
